import { observable, runInAction, toJS } from 'mobx';
import React from 'react';
import noop from 'lodash/noop';
import isObject from 'lodash/isObject';
import isBoolean from 'lodash/isBoolean';
import { categories } from 'choerodon-ui-font';
import { FieldType, RecordStatus } from 'choerodon-ui/pro/lib/data-set/enum';
import { $l } from 'choerodon-ui/pro/lib/locale-context';
import { ColumnAlign, TableQueryBarType } from 'choerodon-ui/pro/lib/table/enum';
import defaultFeedback from 'choerodon-ui/pro/lib/data-set/FeedBack';
import { LabelLayout, ShowValidation } from 'choerodon-ui/pro/lib/form/enum';
import { ButtonColor, FuncType } from 'choerodon-ui/pro/lib/button/enum';
import { defaultExcludeUseColonTag } from 'choerodon-ui/pro/lib/form/utils';
import { Action } from '../trigger/enum';
import { Size } from '../_util/enum';
import Popover from '../popover';
function isCustomizable(target) {
    return isObject(target);
}
const defaultTooltipTheme = target => target === 'validation' ? 'light' : 'dark';
const defaultRenderEmpty = (componentName) => {
    switch (componentName) {
        case 'Attachment':
            return $l('Attachment', 'no_attachments');
        case 'Table':
            return $l('Table', 'empty_data');
        case 'Select':
            return $l('Select', 'no_matching_results');
        case 'Output':
            return '';
        default:
    }
};
const defaultFormFieldHighlightRenderer = ({ content, hidden, ...rest }, element) => content ? (React.createElement(Popover, Object.assign({}, (hidden ? { ...rest, visible: false } : rest), { content: content }), element)) : element;
const defaultButtonProps = { color: ButtonColor.primary, funcType: FuncType.flat };
const defaultSpinProps = { size: Size.default, wrapperClassName: '' };
const defaultTableColumnAlign = (_column, field) => {
    if (field) {
        const { type } = field;
        switch (type) {
            case FieldType.number:
            case FieldType.currency:
                return ColumnAlign.right;
            case FieldType.boolean:
                return ColumnAlign.center;
            default:
        }
    }
};
function getComponentKey(component) {
    switch (component) {
        case 'Tabs':
            return 'tabs';
        default:
            return 'table';
    }
}
const defaultLookupCache = { maxAge: 1000 * 60 * 10, max: 100 };
const defaultStatus = { [RecordStatus.add]: 'add', [RecordStatus.update]: 'update', [RecordStatus.delete]: 'delete' };
const defaultCustomizedSave = (code, customized, component) => localStorage.setItem(`${getComponentKey(component)}.customized.${code}`, JSON.stringify(customized));
const defaultCustomizedLoad = (code, component) => Promise.resolve(JSON.parse(localStorage.getItem(`${getComponentKey(component)}.customized.${code}`) || 'null'));
const defaultFormatter = {
    jsonDate: 'YYYY-MM-DD HH:mm:ss',
    date: 'YYYY-MM-DD',
    dateTime: 'YYYY-MM-DD HH:mm:ss',
    time: 'HH:mm:ss',
    year: 'YYYY',
    month: 'YYYY-MM',
    week: 'GGGG-Wo',
};
const defaultAttachment = {
    defaultFileKey: 'file',
    defaultFileSize: 0,
};
const defaultConfig = {
    prefixCls: 'c7n',
    proPrefixCls: 'c7n-pro',
    iconfontPrefix: 'icon',
    ripple: true,
    collapseExpandIconPosition: 'left',
    collapseTrigger: 'header',
    lookupCache: defaultLookupCache,
    lookupUrl: code => `/common/code/${code}/`,
    lookupAxiosMethod: 'post',
    // lookupBatchAxiosConfig:
    //   codes => ({
    //     url: '/common/batch',
    //     params: codes.reduce((obj, code) => {
    //       obj[code] = code;
    //       return obj;
    //     }, {}),
    //   }),
    lovDefineUrl: code => `/sys/lov/lov_define?code=${code}`,
    lovQueryUrl: code => `/common/lov/dataset/${code}`,
    lovTableProps: {},
    lovModalProps: {},
    lovAutoSelectSingle: false,
    dataKey: 'rows',
    totalKey: 'total',
    statusKey: '__status',
    tlsKey: '__tls',
    status: defaultStatus,
    labelLayout: LabelLayout.horizontal,
    queryBar: TableQueryBarType.normal,
    tableBorder: true,
    tableHighLightRow: true,
    tableSelectedHighLightRow: false,
    tableRowHeight: 30,
    tableDefaultRenderer: '',
    tableColumnResizable: true,
    tableColumnHideable: true,
    performanceTableColumnHideable: true,
    tableRowDraggable: false,
    tableColumnDraggable: false,
    performanceTableColumnDraggable: false,
    tableColumnAlign: defaultTableColumnAlign,
    tableSpinProps: defaultSpinProps,
    tableButtonProps: defaultButtonProps,
    tableCommandProps: defaultButtonProps,
    tableAlwaysShowRowBox: false,
    tableUseMouseBatchChoose: false,
    tableEditorNextKeyEnterDown: true,
    tableAutoFocus: false,
    tableKeyboard: false,
    tableFilterSearchText: 'params',
    tableAutoHeightDiff: 80,
    customizedSave: defaultCustomizedSave,
    customizedLoad: defaultCustomizedLoad,
    tableShowRemovedRow: true,
    modalSectionBorder: true,
    drawerSectionBorder: true,
    drawerTransitionName: 'slide-right',
    modalOkFirst: true,
    modalAutoCenter: false,
    modalKeyboard: true,
    modalMaskClosable: false,
    buttonColor: ButtonColor.default,
    buttonFuncType: FuncType.raised,
    feedback: defaultFeedback,
    renderEmpty: defaultRenderEmpty,
    icons: categories,
    formatter: defaultFormatter,
    dropdownMatchSelectWidth: true,
    selectReverse: true,
    selectPagingOptionContent: '···',
    selectSearchable: false,
    selectTrigger: [Action.focus, Action.click],
    useColon: false,
    excludeUseColonTagList: defaultExcludeUseColonTag,
    numberFieldNonStrictStep: false,
    showInvalidDate: true,
    highlightRenderer: defaultFormFieldHighlightRenderer,
    onPerformance: noop,
    performanceEnabled: { Table: false },
    tooltipTheme: defaultTooltipTheme,
    showValidation: ShowValidation.tooltip,
    attachment: defaultAttachment,
};
const globalConfig = observable.map(defaultConfig);
export function getConfig(key) {
    return globalConfig.get(key);
}
export function getPrefixCls(suffixCls, customizePrefixCls) {
    if (customizePrefixCls) {
        return customizePrefixCls;
    }
    return `${getConfig('prefixCls')}-${suffixCls}`;
}
export function getProPrefixCls(suffixCls, customizePrefixCls) {
    if (customizePrefixCls) {
        return customizePrefixCls;
    }
    return `${getConfig('proPrefixCls')}-${suffixCls}`;
}
export function getCustomizable(component) {
    const customizable = getConfig('customizable');
    if (isBoolean(customizable)) {
        return customizable;
    }
    if (isCustomizable(customizable)) {
        return customizable[component];
    }
}
const mergeProps = ['transport', 'feedback', 'formatter', 'attachment', 'pagination'];
export default function configure(config, merge = true) {
    runInAction(() => {
        Object.keys(config).forEach((key) => {
            const value = config[key];
            if (merge && mergeProps.includes(key) && isObject(value)) {
                globalConfig.set(key, {
                    ...toJS(globalConfig.get(key)),
                    ...value,
                });
            }
            else {
                globalConfig.set(key, config[key]);
            }
        });
    });
}
